import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Field } from "redux-form";
import { ReduxControls } from "rhombus-react";

import { loadAdvertisers } from "reducers/LibraryProperties";
import Validators from "forms/validators/LibraryProperties";
import { CREATE, EDIT, includeUmlauts, tooltipProps } from "./helpers";
import useStateDebounced from "hooks/useStateDebounced";
import useCancelToken from "hooks/useCancelToken";

const advertiserName = {
	 // This is a freeform label until an Advertiser is associated with the library.  Do not confuse with advertiser.name
	name:          "advertiserName",
	label:         "Advertiser Label",
	placeholder:   "Enter Advertiser Name",
	tip:           includeUmlauts,
	required:      true,
	showCharCount: true,
	charMax:       255,
	validate:      Validators.advertiserName,
	component:     ReduxControls.Input,
	props:         { required:true },
};

export default function AdvertiserGroup({ mode, canEditAdvertiser, advertiser, advertiserOptions }){
	let dispatch = useDispatch()
		, mediaBuyerId = useSelector(state => state.libraryProperties.mediaBuyer?.id)
		, isAdvertisersLoaded  = useSelector(state => state.libraryProperties.isAdvertisersLoaded)
		, advertisersPage      = useSelector(state => state.libraryProperties.advertisersPage)
		, advertisersTotalPages = useSelector(state => state.libraryProperties.advertisersTotalPages)
		;

	const [searchTerm, setSearchTerm] = useStateDebounced("");
	const { getNewToken } = useCancelToken(true);

	useEffect(() => {
		if(mode === EDIT && canEditAdvertiser && mediaBuyerId){
			dispatch(loadAdvertisers(mediaBuyerId, { searchTerm }, getNewToken()));
		}
	}, [searchTerm, mode, canEditAdvertiser, mediaBuyerId]);

	function loadNextPage(){
		const hasMore = advertisersPage < advertisersTotalPages;
		if(isAdvertisersLoaded && hasMore){
			dispatch(loadAdvertisers(mediaBuyerId, { searchTerm, page: advertisersPage + 1 }, getNewToken()));
		}
	}

	const advertiserProps = {
		name: "advertiser",
		label: "Advertiser",
		placeholder: "Search and Select ...",
		tip: {
			...tooltipProps,
			description: "Assign an advertiser to the Creative Library. The advertiser cannot be edited after it is assigned.",
		},
		component: ReduxControls.Select,
		parse: value => value?.value,
		props:{
			className: "advertiser-select",
			icon: "search",
			showIcon: true,
			onInputChange: setSearchTerm,
			onMenuScrollToBottom: loadNextPage,
			isLoading: !isAdvertisersLoaded,
		},
	};

	// If we're creating a new library, only the advertiser name field gets show
	if(mode === CREATE){
		return (
			<div className="advertiser-group">
				<Field {...advertiserName} />
			</div>
		);
	}

	// If the advertiser object exists, then we use that field, and we mark it as disabled
	if(advertiser && advertiser.id){
		return (
			<div className="advertiser-group">
				<Field {...advertiserProps} disabled />
			</div>
		);
	}

	// If there is no media buyer object, AND no advertiser, only show the name field
	if(!mediaBuyerId && !advertiser){
		return (
			<div className="advertiser-group">
				<Field {...advertiserName} />
			</div>
		);
	}

	return (
		<div className="advertiser-group">
			<Field {...advertiserName} />
			<Field {...advertiserProps} options={advertiserOptions} />
		</div>
	);
}
